/**
 * scene.js — Three.js core setup
 * Scene, camera, renderer, lights, texture loader, resize.
 * Call initScene() once at app start, then import what you need.
 */

import * as THREE from 'three'
import gsap from 'gsap'

export function initScene() {
    // ── Canvas & Scene ─────────────────────────────────────────
    const canvas = document.querySelector('canvas.webgl')
    const scene = new THREE.Scene()
    const loader = new THREE.TextureLoader()

    // ── Lights ─────────────────────────────────────────────────
    const pointerLight = new THREE.PointLight(0xffffff, 20)
    pointerLight.position.set(0, 0, 2)
    scene.add(pointerLight)
    scene.add(new THREE.AmbientLight(0xffffff, 0.5))

    // Pointer light follows mouse
    const _onMouseMove = (e) => {
        const x =  (e.clientX / window.innerWidth  - 0.5) * 5
        const y = -(e.clientY / window.innerHeight - 0.5) * 5
        gsap.to(pointerLight.position, { x, y, duration: 0.5 })
    }
    window.addEventListener('mousemove', _onMouseMove)

    // ── Sizes & Resize ─────────────────────────────────────────
    const sizes = {
        width:  window.innerWidth,
        height: window.innerHeight,
    }

    // Mobile keyboards change only height — ignore those resize events
    let lastWidth = window.innerWidth
    const _onResize = () => {
        if (window.innerWidth === lastWidth) return
        lastWidth        = window.innerWidth
        sizes.width      = window.innerWidth
        sizes.height     = window.innerHeight

        camera.aspect = sizes.width / sizes.height
        camera.updateProjectionMatrix()

        renderer.setSize(sizes.width, sizes.height)
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    }
    window.addEventListener('resize', _onResize)

    // ── Camera ─────────────────────────────────────────────────
    const camera = new THREE.PerspectiveCamera(45, sizes.width / sizes.height, 0.1, 100)
    camera.position.set(0, 1, 5.5)
    camera.lookAt(0, 1, 0)

    // ── Renderer ───────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        preserveDrawingBuffer: true, // needed for save-as-image feature
    })
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    function destroy() {
        window.removeEventListener('mousemove', _onMouseMove)
        window.removeEventListener('resize',    _onResize)
        renderer.dispose()
    }

    return { scene, camera, renderer, loader, canvas, sizes, destroy }
}
